import React, { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

const benefits = [
  "Access to the daily class · Striking, Grappling, Sparring",
  "Weights & conditioning floor access",
  "No lock-in contract · cancel anytime",
];

export default function Membership() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    preferred_start_date: "",
    interest: "",
    hear_about_us: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/membership/enquiry", form);
      setSubmitted(true);
      toast.success("Enquiry sent. We'll be in touch soon.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send enquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="membership"
      data-testid="membership-section"
      className="relative py-28 md:py-36 bg-[#0a0a0a] border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Pricing Card */}
          <aside className="lg:col-span-5">
            <div className="sticky top-24">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-10 h-px bg-[#d4af37]" />
                <span className="text-xs uppercase tracking-[0.3em] text-[#d4af37] font-bold">
                  Membership
                </span>
              </div>
              <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl uppercase leading-[0.9]">
                All In. <br />
                <span className="text-[#d4af37]">One Rate.</span>
              </h2>

              <div className="mt-10 bg-[#050505] border border-white/10 p-8">
                <div className="flex items-baseline gap-2">
                  <span className="font-heading text-7xl md:text-8xl text-[#d4af37]">$50</span>
                  <span className="text-sm uppercase tracking-[0.2em] text-white/60">/ week</span>
                </div>
                <div className="mt-1 text-xs uppercase tracking-[0.2em] text-white/50">
                  AUD · Weekly · Cancel Anytime
                </div>

                <ul className="mt-8 space-y-3">
                  {benefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-white/85">
                      <span className="mt-1.5 w-2 h-2 bg-[#d4af37] shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>

                <div className="mt-8 border-t border-white/10 pt-5 text-xs text-white/50 leading-relaxed">
                  Drop your details and we'll be in touch to organise a
                  trial session, walk you through the gym, and set up your
                  membership in person.
                </div>
              </div>
            </div>
          </aside>

          {/* Form / Confirmation */}
          {submitted ? (
            <div
              data-testid="membership-confirmation"
              className="lg:col-span-7 bg-[#050505] border border-[#d4af37]/40 p-10 md:p-12 flex flex-col justify-center"
            >
              <div className="text-xs uppercase tracking-[0.3em] text-[#d4af37] font-bold">
                Enquiry Received
              </div>
              <h3 className="mt-3 font-heading text-4xl md:text-5xl uppercase leading-[0.95]">
                Welcome to the <br /> <span className="text-[#d4af37]">family.</span>
              </h3>
              <p className="mt-6 text-white/75 text-base leading-relaxed max-w-xl">
                Cheers, <span className="text-white font-semibold">{form.full_name.split(" ")[0] || "champ"}</span>.
                One of the coaches will reach out within 24 hours to confirm
                your trial session, give you a tour of the gym, and walk you
                through the rest.
              </p>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md">
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setForm({
                      full_name: "",
                      email: "",
                      phone: "",
                      preferred_start_date: "",
                      interest: "",
                      hear_about_us: "",
                    });
                  }}
                  data-testid="membership-new-enquiry"
                  className="px-6 py-3 border border-white/20 text-white/80 text-[11px] font-bold uppercase tracking-[0.25em] hover:border-[#d4af37] hover:text-[#d4af37]"
                >
                  Send another
                </button>
                <a
                  href="#booking"
                  className="px-6 py-3 bg-[#d4af37] text-black text-[11px] font-bold uppercase tracking-[0.25em] hover:bg-[#f1c40f] text-center"
                >
                  Book a private session →
                </a>
              </div>
            </div>
          ) : (
          <form
            onSubmit={onSubmit}
            className="lg:col-span-7 bg-[#050505] border border-white/10 p-8 md:p-10 space-y-8"
            data-testid="membership-form"
          >
            <div>
              <h3 className="font-heading text-3xl md:text-4xl uppercase">Enquire Now</h3>
              <p className="text-sm text-white/60 mt-2">
                Drop your details + a quick note on what you're after.
                We'll be in touch to set you up.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Full Name" id="mb-name">
                <Input
                  id="mb-name"
                  data-testid="membership-full-name"
                  required
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  className="bg-transparent border-white/15 rounded-none h-12 focus-visible:ring-[#d4af37]"
                />
              </Field>
              <Field label="Email" id="mb-email">
                <Input
                  id="mb-email"
                  type="email"
                  data-testid="membership-email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-transparent border-white/15 rounded-none h-12 focus-visible:ring-[#d4af37]"
                />
              </Field>
              <Field label="Phone" id="mb-phone">
                <Input
                  id="mb-phone"
                  data-testid="membership-phone"
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="bg-transparent border-white/15 rounded-none h-12 focus-visible:ring-[#d4af37]"
                />
              </Field>
              <Field label="Preferred Start Date" id="mb-start">
                <Input
                  id="mb-start"
                  type="date"
                  data-testid="membership-start-date"
                  value={form.preferred_start_date}
                  onChange={(e) => setForm({ ...form, preferred_start_date: e.target.value })}
                  className="bg-transparent border-white/15 rounded-none h-12 focus-visible:ring-[#d4af37]"
                />
              </Field>
              <div className="md:col-span-2">
                <Field label="How Did You Hear About Us?" id="mb-hear">
                  <Input
                    id="mb-hear"
                    data-testid="membership-hear-about"
                    placeholder="Instagram, mate's recommendation, walked past…"
                    value={form.hear_about_us}
                    onChange={(e) => setForm({ ...form, hear_about_us: e.target.value })}
                    className="bg-transparent border-white/15 rounded-none h-12 focus-visible:ring-[#d4af37]"
                  />
                </Field>
              </div>
            </div>

            <Field label="Expression of Interest" id="mb-interest">
              <Textarea
                id="mb-interest"
                data-testid="membership-interest"
                rows={5}
                required
                placeholder="What's drawn you to Full Circle? Goals, training experience, anything you'd like us to know."
                value={form.interest}
                onChange={(e) => setForm({ ...form, interest: e.target.value })}
                className="bg-transparent border-white/15 rounded-none focus-visible:ring-[#d4af37]"
              />
            </Field>

            <div className="text-[11px] text-white/45 leading-relaxed border-t border-white/10 pt-5">
              No payment required here — you'll be set up in person on
              your first visit, including signing the digital indemnity
              waiver and choosing how you'd like to pay your weekly fee.
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="membership-submit"
              className="w-full md:w-auto inline-flex items-center justify-center px-10 py-4 bg-[#d4af37] text-black text-xs font-bold uppercase tracking-[0.25em] hover:bg-[#f1c40f] transition-colors disabled:opacity-50 btn-lift"
            >
              {loading ? "Sending…" : "Send Enquiry"}
            </button>
          </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, id, children }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs uppercase tracking-[0.2em] text-white/60">
        {label}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
